import React from 'react';
import { Trash2, Edit3, ExternalLink } from 'lucide-react';

const AcceleratorList = ({ accelerators, onAcceleratorClick, onDelete }) => {
  const getTierColor = (tier) => {
    switch (tier) {
      case 'A': return 'bg-red-100 text-red-800';
      case 'B': return 'bg-orange-100 text-orange-800';
      case 'C': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Researching': return 'bg-blue-100 text-blue-800';
      case 'Applied': return 'bg-yellow-100 text-yellow-800';
      case 'Accepted': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Interview': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="py-4">
      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        {accelerators.map(accelerator => (
          <div
            key={accelerator.id}
            onClick={() => onAcceleratorClick(accelerator)}
            className="bg-white/95 rounded-xl shadow-lg p-4 active:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center text-lg font-bold text-blue-700">
                  {accelerator.name?.[0] || '?'}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{accelerator.name}</div>
                  <div className="text-xs text-gray-500 truncate">{accelerator.type} · {accelerator.location || '—'}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(accelerator.status)}`}>
                  {accelerator.status}
                </span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTierColor(accelerator.tier)}`}>
                  {accelerator.tier}
                </span>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {accelerator.applicationDeadline && <span>Deadline: {accelerator.applicationDeadline}</span>}
              {accelerator.website && (
                <a href={accelerator.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                  Website <ExternalLink size={10} />
                </a>
              )}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onAcceleratorClick(accelerator); }}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
              >
                Edit
              </button>
              {!accelerator.isDemo && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(accelerator.id); }}
                  className="text-red-600 hover:text-red-800 text-xs font-medium"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full bg-white/90 rounded-2xl shadow-lg overflow-hidden">
        <thead className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <tr>
            <th className="px-4 py-4 text-left text-sm font-semibold">Accelerator</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Type</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Location</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Focus</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Stage</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Investment</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Equity</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Tier</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Deadline</th>
            <th className="px-4 py-4 text-left text-sm font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {accelerators.map(accelerator => (
            <tr
              key={accelerator.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onAcceleratorClick(accelerator)}
            >
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center text-lg font-bold text-blue-700">
                    {accelerator.name?.[0] || '?'}
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">{accelerator.name}</div>
                    {accelerator.website && (
                      <a 
                        href={accelerator.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        onClick={e => e.stopPropagation()}
                      >
                        Website <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  {accelerator.isDemo && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.type}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.location}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.focus}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.stage}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.investment}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.equity}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(accelerator.status)}`}>
                  {accelerator.status}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTierColor(accelerator.tier)}`}>
                  {accelerator.tier}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{accelerator.applicationDeadline}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAcceleratorClick(accelerator);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit3 size={16} />
                  </button>
                  {!accelerator.isDemo && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(accelerator.id);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default AcceleratorList; 